import React from 'react';
import { X } from 'lucide-react';
import { usePortfolioItems } from '../hooks/useContent';
import StorageImage from './StorageImage';

interface PortfolioProps {
  isOpen: boolean;
  onClose: () => void;
}

const Portfolio = ({ isOpen, onClose }: PortfolioProps) => {
  const { data: items, isLoading } = usePortfolioItems();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-center justify-center">
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl max-w-6xl w-full relative shadow-2xl">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 bg-white rounded-full p-2 shadow-md transition-all hover:shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 sm:p-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Our Portfolio</h2>
            <p className="text-gray-600 mb-8">Explore our premium glass solutions and installations</p>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600" />
              </div>
            ) : items.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                  >
                    <div className="relative overflow-hidden">
                      <StorageImage
                        path={item.image_path}
                        alt={item.title}
                        className="w-full h-72 object-cover transform transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-12">No portfolio items configured yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
